/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, LoadingOverlay, Paper, PasswordInput, Space, Text } from '@mantine/core';
import { IconLock } from '@tabler/icons';
import { FC, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { AuthS } from '../../Service/restapi/authService';
import { RoutingS } from '../../Service/RoutingS';
import { AlertS } from '../../Service/AlertS';
import PasswordChecker from './PasswordChecker';
import AlertBox from './AlertBox';
import {userActions} from "../../Service/UserAccountActions";
import ReactPixel from "react-facebook-pixel";
import {ConstantS} from "../../Service/ConstantS";
import ReactGA from "react-ga4";
import {MixpanelS} from "../../Service/MixpanelS";
import {ApplicationUserDto} from "../../Types/AuthT";
import GrandientPaper from '../../GradientPaper';

const PasswordResetPage: FC = () => {
  const isPhoneDevice = useMediaQuery('(max-width: 480px)');
  const [searchParams] = useSearchParams();
  const navigate: NavigateFunction = useNavigate();
  const [alert, setAlert] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [validPassword, setValidPassword] = useState<boolean>(false);
  const token = searchParams.get('token');

  const form = useForm({
    initialValues: {
      password: '',
      repeatedPassword: '',
    },
    validate: {
      password: () => (validPassword ? null : 'Bitte geben Sie ein gültiges Passwort ein'),
      repeatedPassword: (value, values) => (value === values.password ? null : 'Passwörter stimmen nicht überein'),
    },
  });

  const resetPassword = () => {
    setLoading(true);
    const { password } = form.values;
    AuthS.resetPassword(token, { password })
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((user : ApplicationUserDto) => {
            if (user.webSessions > 0) {
              navigate(`${RoutingS.ROUTES.LOGIN}?alert=${AlertS.AlertType.PASSWORD_CHANGED}`);
            } else {
              // login new user automatically (ads funnel)
              userActions
                .login(user.email, password)
                .then((resp) => {
                  if (resp.ok) {
                    ReactPixel.trackCustom(ConstantS.TrackingEvents.LoginSuccessful);
                    ReactGA.event({ category: 'User', action: ConstantS.TrackingEvents.LoginSuccessful });
                    MixpanelS.track(ConstantS.TrackingEvents.LoginSuccessful);
                  }
                })
                .catch(() => setAlert(AlertS.AlertType.SERVER_UNAVAILABLE));
            }
          })

        }
      })
      .catch(() => setAlert(AlertS.AlertType.SERVER_UNAVAILABLE))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = 'Passwort zurücksetzen';
    if (token) {
      setLoading(true);
      AuthS.verifyToken(AuthS.TOKEN_TYPE.RESET_PASSWORD, token)
        .then((resp) => {
          if (!resp.ok) {
            navigate(`${RoutingS.ROUTES.PASSWORD_FORGOTTEN}?alert=linkexpired`);
          }
        })
        .catch(() => setAlert(AlertS.AlertType.SERVER_UNAVAILABLE))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GrandientPaper>
      <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
      <Space h="md" />
      <Text size={isPhoneDevice ? 22 : 28} sx={{ lineHeight: 1.2 }}>
        Passwort festlegen
      </Text>
      {alert && <AlertBox alert={alert} />}
      <Space h="xl" />
      <form onSubmit={form.onSubmit(resetPassword)}>
        <PasswordInput
          label={<Text size="xs">Passwort</Text>}
          icon={<IconLock size={16} />}
          {...form.getInputProps('password')}
        />
        <Space h="xs" />
        <PasswordChecker
          password={form.values.password}
          showErrors={Object.keys(form.errors).length > 0}
          setValidPassword={setValidPassword}
        />
        <Space h="xs" />
        <PasswordInput
          label={<Text size="xs">Passwort wiederholen</Text>}
          icon={<IconLock size={16} />}
          {...form.getInputProps('repeatedPassword')}
        />
        <Space h="xl" />
        <Button fullWidth type="submit">
          Passwort festlegen
        </Button>
      </form>
    </GrandientPaper>
  );
};

export default PasswordResetPage;
